import React from "react";
import { Helmet } from "react-helmet-async";

type SeoProps = Omit<React.ComponentProps<typeof Helmet>, "defer">;

/**
 * Per-page <head> tags. Use this instead of <Helmet> directly.
 *
 * It exists to pin one prop: `defer={false}`.
 *
 * react-helmet-async defaults to `defer: true`, which applies head changes
 * inside a requestAnimationFrame. Two things make that default actively
 * broken for client-side navigation:
 *
 *  1. rAF callbacks do not run while a tab is hidden or backgrounded, so the
 *     queued update simply never lands.
 *  2. *Removing* the previous page's tags is not deferred. When the outgoing
 *     page's Helmet unmounts, the instance list is empty, and an empty list
 *     reduces to `defer: undefined` — the falsy branch, which commits
 *     synchronously.
 *
 * Together: navigate in a backgrounded tab and the old title/description/
 * canonical are stripped immediately while the new ones stay queued behind a
 * frame that never comes. The tab is left on a stale title with no meta tags
 * (and no [data-rh] tags at all) until it is focused again and the pending
 * frame finally runs. Committing synchronously also closes the one-frame
 * window on every in-app navigation where the document has no description or
 * canonical.
 *
 * Prerendering is unaffected — `defer` is a client-only concern, and
 * scripts/prerender.mjs still extracts tags from HelmetProvider's context via
 * src/entry-server.tsx.
 *
 * src/tests/seo-head.test.tsx guards both halves of this: that head updates
 * land without rAF, and that no page imports Helmet directly and quietly
 * reintroduces the deferral.
 */
export const Seo: React.FC<SeoProps> = (props) => (
  <Helmet {...props} defer={false} />
);

export default Seo;
